import { useRef, useState } from 'react';
import { Show } from '@components/Show';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { getTemplatesUrl } from '@ContractBuilder/modules/block-edit/views/BlockUsedInTemplatesModal';
import type { LibraryBlockTypeForListPage } from '@ContractBuilder/types';
import { authProvider } from '@helpers/auth';
import { useClosePopoverOnScrollOut } from '@hooks/use-close-popover-on-scroll-out';
import { mdiOpenInNew, mdiTagOutline } from '@mdi/js';
import { Icon } from '@mdi/react';
import { blockQueries } from '@queries/fetchBlocks';
import IconMdi from '@root/src/components/IconMdi';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import pluralize from 'pluralize';

const templatesUrl = getTemplatesUrl();

const countBadgeClasses = clsx(
  'inline-flex',
  'items-center',
  'gap-x-1.5',
  'whitespace-nowrap',
  'rounded-xl',
  'bg-gray-100',
  'px-3',
  'py-1',
  'text-xs',
  'font-medium',
  'text-primary-600',
  'cursor-pointer',
  'hover:bg-gray-200',
  'transition-colors',
);

const ItemUsedWithin = ({ item }: { item: LibraryBlockTypeForListPage }) => {
  const [isPopoverOpen, setPopoverOpen] = useState<boolean>(false);
  const popoverTriggerRef = useRef(null);
  useClosePopoverOnScrollOut(popoverTriggerRef, isPopoverOpen, setPopoverOpen);
  const { data: block, isLoading } = useQuery({
    ...blockQueries.detail(item.id),
    enabled: isPopoverOpen,
  });

  const handleTemplateClick = async (templateId: string | number) => {
    const searchParams = await authProvider.getAuthSearchParams();
    const win = window.open(`${templatesUrl}/${templateId}/contract${searchParams}`);
    return win?.focus();
  };

  if (!item?.usedInTemplatesCount) {
    return null;
  }

  const usedInTemplates = block?.data?.usedInTemplates || [];
  const label = `Used in ${pluralize('this', item.usedInTemplatesCount)} ${pluralize(
    'template',
    item.usedInTemplatesCount,
  )}`;
  const displayText = `${pluralize('Template', item.usedInTemplatesCount, true)}`;

  return (
    <Show when={item.usedInTemplatesCount > 0}>
      <Popover open={isPopoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <span ref={popoverTriggerRef} className={countBadgeClasses}>
            <span>{displayText}</span>
            <Icon path={mdiTagOutline} size={0.5} className="inline-block" />
          </span>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-0">
          <div className="bg-gray-50">
            <div className="flex flex-col gap-4 px-[14px] pt-[14px] pb-6">
              <div className="flex items-center justify-between">
                <p className="text-base leading-6 font-medium text-gray-900">{label}</p>
              </div>
              <div className="h-px w-full bg-gray-300" />
              <div className="flex max-h-96 flex-col gap-3 overflow-y-auto">
                {isLoading
                  ? Array.from({ length: item.usedInTemplatesCount }).map((_, index) => (
                      <div key={`skeleton-${index}`}>
                        <div className="h-4 w-3/4 animate-pulse rounded-md bg-gray-200" />
                      </div>
                    ))
                  : usedInTemplates.map(({ id, name }) => (
                      <div key={id} className="group">
                        <span
                          className="text-primary-500 group-hover:text-primary-600 flex cursor-pointer justify-between gap-1 text-sm leading-4 font-medium"
                          onClick={() => handleTemplateClick(id)}
                        >
                          <span className="overflow-hidden text-ellipsis whitespace-nowrap no-underline hover:underline">
                            {name}
                          </span>
                          <IconMdi
                            className="opacity-75 group-hover:opacity-100"
                            path={mdiOpenInNew}
                            size={0.6}
                            data-testid="block-icon-indicator:linked-library-block"
                          />
                        </span>
                      </div>
                    ))}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </Show>
  );
};

export default ItemUsedWithin;
